import { Container } from 'react-bootstrap';
import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualStep from '../../components/manual/ManualStep';
import ManualAlert from '../../components/manual/ManualAlert';

function ManualConfiguracoesPage() {
  return (
    <Container fluid className="py-4">
      <ManualBreadcrumb 
        items={[
          { label: 'Manual', path: '/painel/manual' },
          { label: 'Configurações', path: '/painel/manual/configuracoes' }
        ]}
      />

      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-gear me-3"></i>
          Configurações do Sistema
        </h1>
        <p className="manual-subtitle">
          Aprenda a gerenciar as tabelas de apoio e configurações do sistema
        </p>
      </div>

      <ManualAlert type="info">
        <strong>O que são Tabelas de Apoio?</strong> São cadastros auxiliares que alimentam 
        as listas e opções disponíveis no sistema, como unidades, situações, órgãos, especialistas, 
        meios de entrada/saída e decisões.
      </ManualAlert>

      <ManualSection icon="bi-shield-lock" title="Permissões Necessárias">
        <p className="mb-4">
          O sistema utiliza <strong>controle de acesso baseado em permissões (RBAC)</strong>.
          Cada tabela de apoio requer uma permissão específica do tipo <code>config.*</code>:
        </p>

        <div className="manual-help-box mb-4">
          <h6 className="mb-3">Permissões de Configuração:</h6>
          <ul className="mb-0">
            <li><code>config.unidades</code> - Gerenciar Unidades</li>
            <li><code>config.situacoes</code> - Gerenciar Situações</li>
            <li><code>config.meios_entrada</code> - Gerenciar Meios de Entrada</li>
            <li><code>config.meios_saida</code> - Gerenciar Meios de Saída</li>
            <li><code>config.decisoes</code> - Gerenciar Decisões</li>
            <li><code>config.modulos</code> - Gerenciar Módulos</li>
            <li><code>config.orgaos</code> - Gerenciar Órgãos</li>
            <li><code>config.especialistas</code> - Gerenciar Especialistas</li>
            <li><code>config.motivos_mba</code> - Gerenciar Motivos MBA</li>
            <li><code>config.promotores</code> - Gerenciar Promotores</li>
            <li><code>config.perfis</code> - Gerenciar Perfis de Usuário</li>
          </ul>
        </div>

        <ManualAlert type="warning">
          <strong>Importante:</strong> Mesmo usuários com perfil Administrador precisam ter as
          permissões específicas <code>config.*</code> para acessar cada tabela. O sistema verifica
          permissões em <strong>dois níveis</strong>:
          <ol className="mt-2 mb-0">
            <li><strong>Proteção de Página:</strong> Impede acesso se não tiver a permissão</li>
            <li><strong>Visibilidade no Menu:</strong> Oculta itens do menu "Tabelas de Apoio" que você não pode acessar</li>
          </ol>
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-building" title="Unidades">
        <p className="mb-4">
          As unidades representam os locais físicos onde os adolescentes podem estar custodiados 
          (UAIPs, Centros de Internação, etc).
        </p>

        <div className="manual-service-card mb-4">
          <h6 className="text-success mb-3">
            <i className="bi bi-plus-circle me-2"></i>
            Como Cadastrar uma Unidade
          </h6>
          <ManualStep number={1}>
            <div>
              <strong>Acesse Tabelas de Apoio → Unidades</strong>
              <p className="mb-0">
                No menu lateral, clique em "Tabelas de Apoio" e depois em "Unidades".
              </p>
            </div>
          </ManualStep>

          <ManualStep number={2}>
            <div>
              <strong>Clique em "+ Nova Unidade"</strong>
              <p className="mb-0">
                Preencha o campo obrigatório <strong>Nome</strong>.
              </p>
            </div>
          </ManualStep>
        </div>

        <ManualAlert type="tip">
          <strong>Dica:</strong> Use nomes padronizados. A unidade é usada em módulos e em
          vínculos operacionais, então duplicidades atrapalham busca e relatórios.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-clipboard-check" title="Situações">
        <p className="mb-4">
          As situações definem o status atual da movimentação. No sistema atual, elas são
          usadas como domínio de leitura para movimentações e não aparecem como item editável
          no menu de Tabelas de Apoio.
        </p>

        <div className="manual-service-card mb-4">
          <h6 className="text-success mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Informações Importantes
          </h6>
          <ul className="mb-0">
            <li><strong>Tipo movimentação:</strong> somente situações desse tipo entram no fluxo de movimentações</li>
            <li><strong>Histórico:</strong> cada nova situação adicionada fica registrada na movimentação</li>
            <li><strong>Situação atual:</strong> o dashboard mostra a situação mais recente da movimentação</li>
          </ul>
        </div>

        <ManualAlert type="warning">
          <strong>Atenção:</strong> Algumas situações são críticas para o funcionamento do sistema 
          (como "Internado" e "Liberado"). Evite alterar essas situações padrão sem alinhamento
          técnico e operacional.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-arrow-right-square" title="Meios de Entrada">
        <p className="mb-4">
          Os meios de entrada definem como o adolescente chegou ao sistema. O cadastro atual usa
          o campo <strong>Nome</strong>.
        </p>

        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="manual-help-box">
              <h6 className="mb-2">
                <i className="bi bi-pencil me-2"></i>
                Campos do Cadastro
              </h6>
              <ul className="mb-0 small">
                <li><strong>Nome:</strong> tipo de entrada exibido nas listas e no dashboard</li>
              </ul>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-help-box">
              <h6 className="mb-2">
                <i className="bi bi-list-check me-2"></i>
                Exemplos Comuns
              </h6>
              <ul className="mb-0 small">
                <li>Apreensão em Flagrante</li>
                <li>Mandado de Busca e Apreensão</li>
                <li>Transferência de Outra Unidade</li>
                <li>Apresentação Espontânea</li>
              </ul>
            </div>
          </div>
        </div>
      </ManualSection>

      <ManualSection icon="bi-arrow-left-square" title="Meios de Saída">
        <p className="mb-4">
          Os meios de saída definem como o adolescente deixou a unidade. O cadastro atual usa
          o campo <strong>Nome</strong>.
        </p>

        <div className="manual-service-card mb-4">
          <h6 className="text-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Diferença entre Meios de Entrada e Saída
          </h6>
          <p className="mb-2">
            <strong>Meios de Entrada:</strong> Usados quando o adolescente <strong>chega</strong> 
            a uma unidade (registro de entrada na movimentação).
          </p>
          <p className="mb-0">
            <strong>Meios de Saída:</strong> Usados quando o adolescente <strong>deixa</strong> 
            a unidade (finalização da movimentação).
          </p>
        </div>

        <div className="manual-help-box">
          <h6 className="mb-2">Exemplos de Meios de Saída:</h6>
          <ul className="mb-0">
            <li>Liberação por Alvará Judicial</li>
            <li>Transferência para Outra Unidade</li>
            <li>Progressão de Medida</li>
            <li>Conclusão da Medida Socioeducativa</li>
            <li>Maioridade</li>
          </ul>
        </div>
      </ManualSection>

      <ManualSection icon="bi-gavel" title="Decisões Judiciais">
        <p className="mb-4">
          As decisões representam os tipos de determinações judiciais que podem ser aplicadas 
          aos adolescentes.
        </p>

        <div className="manual-service-card mb-4">
          <ManualStep number={1}>
            <div>
              <strong>Cadastre as Decisões Possíveis</strong>
              <p className="mb-0">
                Acesse "Tabelas de Apoio → Decisões" e cadastre todos os tipos de decisões 
                judiciais que o sistema precisa registrar (ex: Internação Provisória, Liberdade 
                Assistida, Remissão, etc).
              </p>
            </div>
          </ManualStep>

          <ManualStep number={2}>
            <div>
              <strong>Preencha os Dados</strong>
              <ul className="mb-0">
                <li><strong>Nome:</strong> tipo de decisão judicial exibido nos fluxos de movimentação</li>
              </ul>
            </div>
          </ManualStep>
        </div>
      </ManualSection>

      <ManualSection icon="bi-people" title="Órgãos">
        <p className="mb-4">
          Os órgãos são as instituições envolvidas no processo socioeducativo. O cadastro atual
          usa <strong>Nome do Órgão</strong> e <strong>Sigla</strong>.
        </p>

        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-primary mb-2">
                <i className="bi bi-building me-2"></i>
                Exemplos de Órgãos
              </h6>
              <ul className="mb-0 small">
                <li>Vara da Infância e Juventude</li>
                <li>Ministério Público do Distrito Federal</li>
                <li>Defensoria Pública do Distrito Federal</li>
                <li>Delegacias da Criança e do Adolescente</li>
                <li>Secretaria de Justiça</li>
              </ul>
            </div>
          </div>

          <div className="col-md-6">
            <div className="manual-service-card">
              <h6 className="text-success mb-2">
                <i className="bi bi-card-checklist me-2"></i>
                Campos do Cadastro
              </h6>
              <ul className="mb-0 small">
                <li>Nome do Órgão</li>
                <li>Sigla (ex: MPDFT, DPDF)</li>
              </ul>
            </div>
          </div>
        </div>

        <ManualAlert type="tip">
          <strong>Cadastro Completo:</strong> Preencha todas as informações de contato dos órgãos. 
          Isso facilita a comunicação e a geração de ofícios e relatórios.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-person-badge" title="Especialistas">
        <p className="mb-4">
          Os especialistas são os profissionais selecionados nos atendimentos de especialistas.
          O cadastro atual usa <strong>Nome do Especialista</strong>.
        </p>

        <div className="manual-service-card mb-4">
          <h6 className="text-info mb-3">
            <i className="bi bi-plus-circle me-2"></i>
            Cadastrando Especialistas
          </h6>
          
          <ManualStep number={1}>
            <div>
              <strong>Acesse Tabelas de Apoio → Especialistas</strong>
              <p className="mb-0">
                Clique em "+ Novo Especialista" para abrir o formulário.
              </p>
            </div>
          </ManualStep>

          <ManualStep number={2}>
            <div>
              <strong>Preencha os Dados Profissionais</strong>
              <p className="mb-0">
                Informe o nome padronizado do profissional para evitar duplicidade na seleção
                dos atendimentos.
              </p>
            </div>
          </ManualStep>
        </div>

        <ManualAlert type="info">
          <strong>Importante:</strong> Especialistas precisam estar cadastrados nas tabelas de 
          apoio para que possam ser vinculados aos atendimentos. Não confunda com "Usuários" 
          (que têm login no sistema).
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-list-ul" title="Gerenciando Ordens nas Listas">
        <p className="mb-4">
          Algumas listas são ordenadas pelo backend e outras alfabeticamente. Nas telas atuais de
          cadastro de apoio, o usuário normalmente informa apenas os campos essenciais de cada domínio.
        </p>

        <div className="manual-help-box mb-4">
          <h6 className="mb-3">Como manter consistência:</h6>
          <ul className="mb-0">
            <li>Use grafia padronizada</li>
            <li>Pesquise antes de cadastrar para evitar duplicidade</li>
            <li>Evite abreviações diferentes para o mesmo conceito</li>
            <li>Considere impacto em relatórios antes de renomear itens usados</li>
          </ul>
        </div>
      </ManualSection>

      <ManualSection icon="bi-clipboard-data" title="Motivos MBA">
        <p className="mb-4">
          A tabela de <strong>Motivos MBA</strong> é usada nas movimentações para detalhar os
          contextos relacionados ao fluxo de MBA.
        </p>

        <div className="manual-help-box">
          <ul className="mb-0">
            <li>Mantenha os motivos padronizados para melhorar relatórios e indicadores.</li>
            <li>Evite duplicidade de nomes com grafias diferentes.</li>
            <li>Evite excluir itens que já tenham sido usados em movimentações históricas.</li>
          </ul>
        </div>
      </ManualSection>

      <ManualSection icon="bi-person-vcard" title="Promotores">
        <p className="mb-4">
          A tabela de <strong>Promotores</strong> abastece os fluxos de atendimento e registro
          vinculados ao Ministério Público.
        </p>

        <div className="manual-service-card">
          <ul className="mb-0">
            <li>Cadastre nomes com padronização para evitar duplicidades.</li>
            <li>Mantenha dados atualizados para garantir consistência dos atendimentos.</li>
            <li>Use edição quando houver correção de grafia ou padronização.</li>
          </ul>
        </div>
      </ManualSection>

      <ManualSection icon="bi-trash" title="Excluindo Registros">
        <div className="manual-troubleshoot">
          <h6 className="text-danger mb-3">
            <i className="bi bi-exclamation-octagon me-2"></i>
            Cuidado ao Excluir!
          </h6>
          <p className="mb-2">
            <strong>Você NÃO pode excluir registros que estão sendo usados.</strong> Por exemplo:
          </p>
          <ul className="mb-3">
            <li>Uma unidade que já recebeu adolescentes</li>
            <li>Uma situação vinculada a movimentações</li>
            <li>Um meio de entrada usado em registros anteriores</li>
            <li>Um especialista que já fez atendimentos</li>
          </ul>
          <p className="mb-0">
            <strong>Solução:</strong> se a exclusão for bloqueada por vínculo histórico, mantenha o
            registro e ajuste apenas a nomenclatura quando necessário.
          </p>
        </div>
      </ManualSection>

      <ManualSection icon="bi-shield-check" title="Boas Práticas">
        <div className="manual-help-box">
          <h6 className="mb-3">Recomendações importantes:</h6>
          <ul className="mb-0">
            <li className="mb-2">
              <strong>Planeje antes de cadastrar:</strong> Discuta com a equipe quais opções 
              realmente precisam estar no sistema
            </li>
            <li className="mb-2">
              <strong>Mantenha a consistência:</strong> Use nomenclaturas padronizadas e claras
            </li>
            <li className="mb-2">
              <strong>Revise periodicamente:</strong> Verifique se há itens duplicados ou em desuso
            </li>
            <li className="mb-2">
              <strong>Documente mudanças:</strong> Mantenha registro de alterações importantes 
              nas configurações
            </li>
            <li className="mb-2">
              <strong>Teste após alterações:</strong> Sempre teste o sistema após modificar 
              tabelas de apoio importantes
            </li>
          </ul>
        </div>
      </ManualSection>

      <ManualAlert type="important">
        <strong>Permissão Necessária:</strong> o acesso a cada tabela depende da permissão
        <code>config.*</code> correspondente. Se você não vê uma opção no menu, seu perfil não
        possui aquela permissão.
      </ManualAlert>
    </Container>
  );
}

export default ManualConfiguracoesPage;
